import { Separator } from '~/primitives/separator';
import { ScrollArea } from '~/primitives/scroll-area';
import ProfileSettingsForm from './-ProfileSettings';
import MergeRequestSubmitter from './-MergeRequestSubmitter';
import ApiKeySettings from './-ApiKeySettings';
import ThemeColorSettings from './-ThemeColorSettings';
import DisplaySettings from './-DisplaySettings';
import { createFileRoute } from '@tanstack/react-router';
import { seo } from '~/util';
import {
    userConnectedAccountsQuery,
    userMeQuery,
} from '~/util/queries/public/users';
import ConnectedAccounts from './-ConnectedAccounts';
import { userProfileQuery } from '~/util/queries/public/profiles';

export const Route = createFileRoute('/_authenticated/settings')({
    loader: async ({
        context: {
            queryClient,
            auth: { id },
        },
    }) => {
        const [user, classic, platformer, connectedAccounts] =
            await Promise.all([
                queryClient.fetchQuery(userMeQuery()),
                queryClient.fetchQuery(
                    userProfileQuery({ list: 'classic', userid: id }),
                ),
                queryClient.fetchQuery(
                    userProfileQuery({ list: 'platformer', userid: id }),
                ),
                queryClient.fetchQuery(
                    userConnectedAccountsQuery({ user_id: id }),
                ),
            ]);

        return { user, classic, platformer, connectedAccounts };
    },
    head: () => ({
        meta: seo({
            title: 'Settings',
            description: 'Manage your AREDL account and website settings',
        }),
    }),
    component: Settings,
});

function Settings() {
    return (
        <div className="w-full max-w-screen h-[calc(100vh-150px)] bg-background-dark rounded-xl flex flex-col box-border wrap-break-word p-2 sm:p-4 md:p-6 lg:p-8 overflow-x-hidden">
            <ScrollArea className="w-full h-[calc(100vh-150px)] overflow-x-hidden">
                <div className="pt-2 pb-2">
                    <div className="py-6">
                        <h1 className="text-secondary-foreground text-2xl sm:text-3xl lg:text-5xl font-bold mb-6 sm:mb-8 text-center wrap-break-word">
                            Settings
                        </h1>

                        <div className="w-full overflow-x-auto px-0.5 sm:px-0">
                            <main className="flex flex-col gap-6 sm:gap-10 w-full max-w-full sm:max-w-6xl mx-auto px-2 sm:px-4 overflow-x-hidden box-border">
                                {/* profile settings */}
                                <section className="w-full max-w-full flex flex-col gap-3 sm:gap-4 p-4 sm:p-6 box-border rounded-lg border border-border bg-background shadow-sm overflow-x-hidden wrap-break-word">
                                    <h2 className="text-xl sm:text-2xl font-semibold text-secondary-foreground">
                                        Profile
                                    </h2>
                                    <Separator className="w-12 sm:w-16 bg-secondary-foreground" />
                                    <ProfileSettingsForm />
                                </section>

                                {/* merge + thumbnails + theme */}
                                <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 w-full items-stretch min-w-0 max-w-full box-border">
                                    <div className="w-full lg:w-[70%] flex flex-col min-w-0 justify-center max-w-full box-border">
                                        <div className="flex flex-col gap-4 sm:gap-6">
                                            <section className="w-full max-w-full flex flex-col gap-3 sm:gap-4 p-4 sm:p-6 box-border rounded-lg border border-border bg-background shadow-sm overflow-x-hidden">
                                                <h2 className="text-xl sm:text-2xl font-semibold text-secondary-foreground">
                                                    Merge Request
                                                </h2>
                                                <Separator className="w-12 sm:w-16 bg-secondary-foreground" />
                                                <MergeRequestSubmitter />
                                            </section>

                                            <section className="w-full max-w-full flex flex-col gap-3 sm:gap-4 p-4 sm:p-6 box-border rounded-lg border border-border bg-background shadow-sm overflow-x-hidden">
                                                <h2 className="text-xl sm:text-2xl font-semibold text-secondary-foreground">
                                                    Display Settings
                                                </h2>
                                                <Separator className="w-12 sm:w-16 bg-secondary-foreground" />
                                                <DisplaySettings />
                                            </section>
                                        </div>
                                    </div>

                                    <section className="w-full max-w-full lg:w-[30%] flex flex-col gap-3 sm:gap-4 p-4 sm:p-6 box-border rounded-lg border border-border bg-background shadow-sm min-w-0 self-stretch overflow-x-hidden">
                                        <h2 className="text-xl sm:text-2xl font-semibold text-secondary-foreground">
                                            Website Theme
                                        </h2>
                                        <Separator className="w-12 sm:w-16 bg-secondary-foreground" />
                                        <ThemeColorSettings />
                                    </section>
                                </div>
                                <section className="w-full max-w-full flex flex-col gap-3 sm:gap-4 p-4 sm:p-6 box-border rounded-lg border border-border bg-background shadow-sm overflow-x-hidden">
                                    <h2 className="text-xl sm:text-2xl font-semibold text-secondary-foreground">
                                        Connected Accounts
                                    </h2>
                                    <Separator className="w-12 sm:w-16 bg-secondary-foreground" />
                                    <ConnectedAccounts />
                                </section>

                                {/* developer settings */}
                                <section className="w-full max-w-full flex flex-col gap-3 sm:gap-4 p-4 sm:p-6 box-border rounded-lg border border-border bg-background shadow-sm overflow-x-hidden">
                                    <h2 className="text-xl sm:text-2xl font-semibold text-secondary-foreground">
                                        Developer Settings
                                    </h2>
                                    <Separator className="w-12 sm:w-16 bg-secondary-foreground" />
                                    <ApiKeySettings />
                                </section>
                            </main>
                        </div>
                    </div>
                </div>
            </ScrollArea>
        </div>
    );
}
